/**
 * SMS_NUMBER_BUSY handling for contact writers (two-way SMS plan §4.3).
 *
 * The `zz_sms_10_gate_prepare` trigger on `contacts` try-locks a per-phone-number gate on every
 * contact INSERT/DELETE and every UPDATE that changes the normalized phone, sms_opt_in,
 * sms_opt_out_at, merged_into or status. When another transaction holds that number's gate the
 * statement aborts with SQLSTATE 55P03 and a message starting `SMS_NUMBER_BUSY`. The trigger aborts
 * the WHOLE statement, so nothing was written and re-running the identical statement is safe.
 *
 * Retry ONLY that refusal, a bounded number of times. Every other error is returned unchanged:
 * business refusals (P0001), unrelated lock errors (55P03 without the marker), and network errors
 * (no code; a create may already have committed, so a blind retry could duplicate it).
 */

export const SMS_NUMBER_BUSY_RETRIES = 3;

/** Shown to a person when the bounded retry is exhausted. */
export const SMS_NUMBER_BUSY_MESSAGE =
  "This phone number is being updated by someone else. Try again in a moment.";

export function isSmsNumberBusy(error: unknown): boolean {
  const e = error as { code?: unknown; message?: unknown } | null;
  return (
    e?.code === "55P03" &&
    typeof e.message === "string" &&
    e.message.includes("SMS_NUMBER_BUSY")
  );
}

/**
 * Run a Supabase `{ data, error }` call, re-running it while it returns SMS_NUMBER_BUSY, at most
 * SMS_NUMBER_BUSY_RETRIES times with linear backoff. The last result is returned either way, so the
 * caller's existing error handling sees an exhausted busy refusal exactly as it would see any error.
 */
export async function retryOnSmsNumberBusy<T extends { error: unknown }>(
  run: () => PromiseLike<T>,
  delayMs = 200,
): Promise<T> {
  let result = await run();
  for (
    let attempt = 1;
    attempt <= SMS_NUMBER_BUSY_RETRIES && isSmsNumberBusy(result.error);
    attempt += 1
  ) {
    await new Promise((r) => setTimeout(r, delayMs * attempt));
    result = await run();
  }
  return result;
}

/**
 * For call sites that ALREADY wrap a call in a transient-error retry (`callWithRetry`, whose
 * transient set includes every 55P03): hand a busy refusal straight to the bounded busy retry
 * instead of letting the transient retry spend its attempts on it too, so busy is retried at most
 * SMS_NUMBER_BUSY_RETRIES times in total and every non-busy error keeps exactly the transient
 * policy it had before. `withTransientRetry` receives a call that never reports a busy error.
 */
export async function retryOnSmsNumberBusyAround<T extends { error: unknown }>(
  call: () => PromiseLike<T>,
  withTransientRetry: (shielded: () => Promise<T>) => Promise<T>,
  delayMs = 200,
): Promise<T> {
  return retryOnSmsNumberBusy(async () => {
    let busy: T | null = null;
    const res = await withTransientRetry(async () => {
      const r = await call();
      if (isSmsNumberBusy(r.error)) {
        busy = r;
        return { ...r, error: null };
      }
      busy = null;
      return r;
    });
    return busy ?? res;
  }, delayMs);
}
